import { useQuery } from "@apollo/client";
import { compact, orderBy } from "lodash";
import moment from "moment";
import { useMemo, useRef } from "react";
import {
  GetPreviousMeetingsQueryQuery,
  GetPreviousMeetingsQueryQueryVariables,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { assertEdgesNonNull, removeCurrentYear } from "@helpers/helpers";

import getPreviousMeetingsQuery from "../graphql/get-previous-meetings-query";
import { TocMeeting } from "./meeting-page-toc-helpers";

// Vertical "table of contents" pinned at the left edge of the meeting content.
//
// Layout (top to bottom):
//   - calendar dropdown (browse all meetings in the series)
//   - up chevron (one date newer than the active meeting)
//   - down chevron (one date older than the active meeting)
//   - lines container (expands on hover into a white card with dates)
//
// The top three buttons are always icon-sized and never trigger the popover —
// only hovering the lines container does. The active meeting's line is
// rendered darker / bolder.
const MeetingPageTOC = ({
  displayedMeetings,
  activeMeetingId,
  onLoadMeeting,
  onScrollToMeeting,
  loadingMeetingId = null,
  subjectId = null,
}: {
  displayedMeetings: TocMeeting[];
  activeMeetingId: number | null;
  onLoadMeeting: (target: TocMeeting) => void;
  onScrollToMeeting: (meetingId: number) => void;
  // Meeting whose data is still being fetched after a click — its line in
  // the TOC gets a spinner so the user knows the click is in flight even
  // though the page hasn't scrolled yet.
  loadingMeetingId?: number | null;
  // On the profile 1-on-1 tab, the navigable series is the person's 1-on-1s as
  // the subject across every MeetingGroup (matching the list the tab shows);
  // everywhere else it's the current meeting's own MeetingGroup series.
  subjectId?: number | null;
}) => {
  const meetingGroupId =
    displayedMeetings.find((m) => !!m.meetingGroupId)?.meetingGroupId ?? null;
  const scopeBySubject = subjectId !== null && subjectId !== undefined;

  const endOfToday = useMemo(() => moment().endOf("day").format(), []);
  const maxFutureDate = useMemo(
    () => moment().add(2, "month").endOf("day").format(),
    [],
  );

  const { data, loading, fetchMore } = useQuery<
    GetPreviousMeetingsQueryQuery,
    GetPreviousMeetingsQueryQueryVariables
  >(getPreviousMeetingsQuery, {
    variables: {
      beforeDate: endOfToday,
      maxFutureDate,
      // Exactly one of subject / meetingGroupId scopes the series; the other is
      // null so its filter is skipped. When subject-scoped, include drafts /
      // ignored (null = no filter) so ad-hoc & imported 1-on-1s are reachable,
      // matching the profile list; the group series keeps them filtered out.
      meetingGroupId: scopeBySubject ? null : (meetingGroupId ?? -1),
      subject: scopeBySubject ? subjectId : null,
      draft: scopeBySubject ? null : false,
      ignored: scopeBySubject ? null : false,
      limit: 15,
      after: null,
    },
    skip: scopeBySubject ? false : meetingGroupId === null,
  });

  const pastMeetings = data?.meetings ? assertEdgesNonNull(data.meetings) : [];
  const nextMeetings = data?.nextMeetings
    ? assertEdgesNonNull(data.nextMeetings)
    : [];
  // Series sorted newest → oldest, used by the chevrons to step through the
  // full timeline (past + future).
  const seriesMeetings = orderBy(
    compact([...pastMeetings, ...nextMeetings]),
    ["startDatetime"],
    ["desc"],
  );

  // The lines list mirrors the calendar dropdown's source: the single nearest
  // future meeting at the top, then past meetings (newest first). The list is
  // scrollable (see max-h on the container below) and offers a "Load more"
  // button to fetch older meetings, matching the dropdown's pagination.
  const firstFutureMeeting = nextMeetings.at(0);
  // First past meeting that isn't today — used to tag the "Last Meeting"
  // badge (same logic as the calendar dropdown).
  const firstMeetingInThePast = pastMeetings.find(
    (m) => !moment().isSame(m.startDatetime, "day"),
  );
  const dropdownSeriesMeetings: TocMeeting[] = compact([
    firstFutureMeeting,
    ...pastMeetings,
  ]).map((m) => ({
    id: m.id,
    startDatetime: m.startDatetime,
    meetingGroupId: m.meetingGroupId,
  }));
  // Union in any displayed meetings the query window doesn't cover (a series
  // with more than `limit` past meetings, or a multi-group 1-on-1 whose
  // timeline crosses meetingGroupId) so every on-screen meeting has a line and
  // can be highlighted as active. Sorted newest-first to match the page order.
  const seenMeetingIds = new Set(dropdownSeriesMeetings.map((m) => m.id));
  const meetingsToShow: TocMeeting[] = orderBy(
    [
      ...dropdownSeriesMeetings,
      ...displayedMeetings.filter((m) => !seenMeetingIds.has(m.id)),
    ],
    [(m) => m.startDatetime ?? ""],
    ["desc"],
  );

  const displayedIdSet = new Set(displayedMeetings.map((m) => m.id));

  // Chevron navigation: one step relative to whatever meeting the user is
  // currently looking at. Falls back to the topmost displayed meeting before
  // the user has scrolled.
  const orderedDisplayed = orderBy(
    displayedMeetings,
    [(m) => m.startDatetime ?? ""],
    ["desc"],
  );
  const topmost = orderedDisplayed[0];
  const referenceMeetingId = activeMeetingId ?? topmost?.id ?? null;
  const referenceIndex =
    referenceMeetingId !== null
      ? seriesMeetings.findIndex((m) => m.id === referenceMeetingId)
      : -1;
  const newerTarget =
    referenceIndex > 0 ? seriesMeetings[referenceIndex - 1] : undefined;
  const olderTarget =
    referenceIndex !== -1 && referenceIndex < seriesMeetings.length - 1
      ? seriesMeetings[referenceIndex + 1]
      : undefined;

  const handleLineClick = (m: TocMeeting) => {
    if (displayedIdSet.has(m.id)) {
      onScrollToMeeting(m.id);
    } else {
      onLoadMeeting(m);
    }
  };

  // Calendar dropdown options: shared with PreviousMeetingsPopoverPanel so
  // both calendar icons surface the same list (next meeting + past meetings,
  // paginated via "Load more") with identical styling. Clicking inline-loads
  // via the TOC's handleLineClick path.
  const loadingRef = useRef(false);
  const hasMorePreviousMeetings = !!data?.meetings?.pageInfo?.hasNextPage;
  const endCursorMorePreviousMeetings = data?.meetings?.pageInfo?.endCursor;
  const handleFetchMorePreviousMeetings = () => {
    if (loadingRef.current || !hasMorePreviousMeetings) return;
    loadingRef.current = true;
    fetchMore({
      variables: { after: endCursorMorePreviousMeetings, merge: true },
    })
      .then(() => {
        loadingRef.current = false;
      })
      .catch(() => {
        loadingRef.current = false;
      });
  };

  // Hide the TOC when there's nothing to move between: a single meeting (a lone
  // draft / one-off) has no series to navigate.
  if (meetingsToShow.length <= 1) return null;

  return (
    // `relative` so the absolutely-positioned lines container is anchored to
    // the icons column below them. Keeping the lines absolute means their
    // expand-on-hover doesn't widen this column and shift the icons.
    <div className="relative flex flex-col items-center gap-0.5">
      {/* The calendar dropdown is intentionally not rendered here — on small
          screens (where the TOC is hidden via `hidden md:block` on the
          wrapper) the calendar dropdown still appears inline in the meeting
          header via PreviousMeetingsPopoverPanel, so users always have one
          way to browse the full series. */}
      {/* Up chevron — one date newer than active. When the user has just
          clicked it and the target meeting is still loading, swap the icon
          for a spinner so the click registers visually. */}
      {newerTarget && newerTarget.id === loadingMeetingId ? (
        <div className="flex h-6 w-6 items-center justify-center">
          <Loading mini size="3" />
        </div>
      ) : (
        <Button
          disabled={!newerTarget}
          size={ButtonSize.medium}
          theme={buttonTheme.iconGray}
          className="!text-gray-500"
          icon={getTopicflowIcon("chevronUp")}
          aria-label="Newer meeting"
          onClick={(e: any) => {
            e?.preventDefault?.();
            if (!newerTarget) return;
            onLoadMeeting({
              id: newerTarget.id,
              startDatetime: newerTarget.startDatetime,
              meetingGroupId: newerTarget.meetingGroupId ?? meetingGroupId!,
            });
          }}
        />
      )}
      {/* Down chevron — one date older than active. Same spinner-swap as
          the up chevron when its target is loading. */}
      {olderTarget && olderTarget.id === loadingMeetingId ? (
        <div className="flex h-6 w-6 items-center justify-center">
          <Loading mini size="3" />
        </div>
      ) : (
        <Button
          disabled={!olderTarget}
          size={ButtonSize.medium}
          theme={buttonTheme.iconGray}
          className="!text-gray-500"
          icon={getTopicflowIcon("chevronDown")}
          aria-label="Older meeting"
          onClick={(e: any) => {
            e?.preventDefault?.();
            if (!olderTarget) return;
            onLoadMeeting({
              id: olderTarget.id,
              startDatetime: olderTarget.startDatetime,
              meetingGroupId: olderTarget.meetingGroupId ?? meetingGroupId!,
            });
          }}
        />
      )}
      {/* Lines container — absolutely positioned just below the icons. In
          collapsed state its width matches the icon column (24px) so the
          lines sit on the same vertical axis as the chevrons. On hover it
          grows rightward to fit the date text. */}
      <div
        className={[
          // Collapsed: w-6 (24px), matching the chevron icon column above
          // so the lines sit on the same vertical axis. Expanded (hover):
          // a fixed w-32 (128px) — just enough for the date column and
          // the right-aligned Today/Next/Last text with a tiny gap.
          // Anchored by its right edge so the hover-expansion grows
          // LEFTWARD (over the meeting content).
          "absolute top-full right-0 mt-2 w-6 hover:w-32",
          "group flex flex-col items-stretch rounded-md",
          "border border-transparent bg-transparent shadow-none py-1",
          "transition-all duration-200 ease-out",
          // Asymmetric expanded padding so the scrollbar lives close to
          // the right edge of the popover while the date text gets normal
          // breathing room on the left.
          "hover:bg-white hover:border-gray-200 hover:shadow-md hover:pl-3 hover:pr-1",
        ].join(" ")}
        aria-label="Meeting list"
      >
        <div className="flex flex-col items-stretch gap-0.5 max-h-60 overflow-y-hidden group-hover:overflow-y-scroll scrollbar-minimal">
          {meetingsToShow.map((m) => {
            const dateLabel = m.startDatetime
              ? removeCurrentYear(moment(m.startDatetime).format("ll"))
              : "Meeting";
            const isActive = m.id === activeMeetingId;
            const isLoading = m.id === loadingMeetingId;
            const badge =
              m.startDatetime && moment().isSame(m.startDatetime, "day")
                ? "Today"
                : m.startDatetime && moment().isBefore(m.startDatetime, "day")
                  ? "Next"
                  : firstMeetingInThePast?.id === m.id
                    ? "Last"
                    : null;
            return (
              <button
                key={m.id}
                type="button"
                aria-label={`Jump to ${dateLabel}`}
                title={dateLabel}
                onClick={() => handleLineClick(m)}
                className={[
                  "flex items-center h-5 rounded cursor-pointer shrink-0",
                  "justify-center group-hover:justify-start group-hover:px-1 group-hover:gap-1",
                  "hover:bg-gray-100 transition-colors",
                ].join(" ")}
              >
                {/* Collapsed indicator — a small dash when the meeting is
                    ready, or a spinner while its row is still loading
                    inline. The line is hidden once the popover expands. */}
                {isLoading ? (
                  <span className="block group-hover:hidden" aria-hidden="true">
                    <Loading mini size="3" />
                  </span>
                ) : (
                  <span
                    className={[
                      "block h-0.5 rounded group-hover:hidden transition-colors",
                      isActive ? "w-2 bg-gray-700" : "w-1 bg-gray-300",
                    ].join(" ")}
                    aria-hidden="true"
                  />
                )}
                {/* Date column ("Jun 16") — fixed width + text-left so each
                    row's date starts at the same x position. */}
                <span
                  className={[
                    "hidden group-hover:inline-block w-14 shrink-0 whitespace-nowrap text-xs text-left",
                    isActive ? "text-gray-900 font-semibold" : "text-gray-700",
                  ].join(" ")}
                >
                  {dateLabel}
                </span>
                {badge && (
                  <span className="hidden group-hover:inline-block ml-auto tf-header-xs-light text-gray-500 whitespace-nowrap">
                    {badge}
                  </span>
                )}
                {isLoading && (
                  <Loading
                    mini
                    size="3"
                    className="hidden group-hover:inline-block ml-auto shrink-0"
                  />
                )}
              </button>
            );
          })}
          {/* "Load more" sits as the last item in the scrollable list, only
              visible when the popover is expanded and there are older
              meetings left to fetch. */}
          {hasMorePreviousMeetings && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleFetchMorePreviousMeetings();
              }}
              className="hidden group-hover:flex items-center justify-start gap-1 h-5 px-1 text-xs text-gray-600 hover:bg-gray-100 rounded cursor-pointer whitespace-nowrap shrink-0"
            >
              <span>Load more</span>
              {loading && <Loading mini size="4" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingPageTOC;
